import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator, Platform,
} from 'react-native';
import { RD, fonts } from '../theme/colors';
import { loadGoogleMapsScript } from '../utils/googleMaps';

interface Prediction {
  place_id: string;
  description: string;
}

interface Props {
  value: string;
  onSelect: (address: string, lat: number, lng: number) => void;
  onError?: (message: string) => void;
  placeholder?: string;
}

export function AddressAutocomplete({ value, onSelect, onError, placeholder }: Props) {
  const [query, setQuery] = useState(value);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [showList, setShowList] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const serviceRef = useRef<any>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    loadGoogleMapsScript(() => {
      const google = (window as any).google;
      if (google?.maps?.places) {
        serviceRef.current = new google.maps.places.AutocompleteService();
      }
    });
  }, []);

  const fetchPredictions = useCallback(async (text: string) => {
    if (text.length < 3) { setPredictions([]); setShowList(false); return; }
    setLoading(true);

    if (Platform.OS === 'web' && serviceRef.current) {
      serviceRef.current.getPlacePredictions(
        { input: text, types: ['address'], componentRestrictions: { country: 'us' } },
        (results: any[] | null, status: string) => {
          if (status === 'OK' && results) {
            const mapped = results.map((r: any) => ({
              place_id: r.place_id,
              description: r.description,
            }));
            setPredictions(mapped);
            setShowList(mapped.length > 0);
          } else {
            setPredictions([]);
            setShowList(false);
          }
          setLoading(false);
        },
      );
    } else {
      try {
        const url = `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(text)}&types=address&components=country:us&key=AIzaSyBWssHGC4J0PqxNbWza5KAXsF5V_g-q0Xg`;
        const res = await fetch(url);
        const data = await res.json();
        const mapped = (data.predictions || []).map((p: any) => ({
          place_id: p.place_id,
          description: p.description,
        }));
        setPredictions(mapped);
        setShowList(mapped.length > 0);
      } catch {
        setPredictions([]);
      }
      setLoading(false);
    }
  }, []);

  const handleChange = (text: string) => {
    setQuery(text);
    selectedRef.current = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(text), 300);
  };

  const selectedRef = useRef(false);

  const blurTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleBlur = useCallback(() => {
    // Delay to let dropdown tap register first (mobile fires blur before touch)
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => {
      if (selectedRef.current || !query || query.length < 5) return;
      if (Platform.OS === 'web' && (window as any).google?.maps) {
        const geocoder = new (window as any).google.maps.Geocoder();
        geocoder.geocode({ address: query }, (results: any[], status: string) => {
          if (selectedRef.current) return; // selected while geocoding
          if (status === 'OK' && results[0]) {
            const loc = results[0].geometry.location;
            onSelect(results[0].formatted_address || query, loc.lat(), loc.lng());
          }
          // Don't show error on blur — let form submit handle validation
        });
      } else {
        fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=AIzaSyBWssHGC4J0PqxNbWza5KAXsF5V_g-q0Xg`)
          .then(r => r.json())
          .then(data => {
            if (selectedRef.current) return;
            const loc = data.results?.[0]?.geometry?.location;
            if (loc?.lat && loc?.lng) {
              onSelect(data.results?.[0]?.formatted_address || query, loc.lat, loc.lng);
            }
          })
          .catch(() => {});
      }
    }, 400);
  }, [query, onSelect]);

  const handleSelect = async (prediction: Prediction) => {
    selectedRef.current = true;
    setQuery(prediction.description);
    setShowList(false);
    setPredictions([]);

    if (Platform.OS === 'web' && (window as any).google?.maps) {
      const geocoder = new (window as any).google.maps.Geocoder();
      geocoder.geocode({ placeId: prediction.place_id }, (results: any[], status: string) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          onSelect(prediction.description, loc.lat(), loc.lng());
        } else {
          onError?.('Could not get coordinates for this address. Please try a different address.');
        }
      });
    } else {
      try {
        const url = `https://maps.googleapis.com/maps/api/geocode/json?place_id=${prediction.place_id}&key=AIzaSyBWssHGC4J0PqxNbWza5KAXsF5V_g-q0Xg`;
        const res = await fetch(url);
        const data = await res.json();
        const loc = data.results?.[0]?.geometry?.location;
        if (loc?.lat && loc?.lng) {
          onSelect(prediction.description, loc.lat, loc.lng);
        } else {
          onError?.('Could not get coordinates for this address. Please try a different address.');
        }
      } catch {
        onError?.('Could not get coordinates for this address. Please try a different address.');
      }
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.inputWrap}>
        <View style={styles.searchIconWrap}>
          <Text style={styles.searchIcon}>{'⌕'}</Text>
        </View>
        <TextInput
          style={styles.input}
          placeholder={placeholder || 'Start typing your address...'}
          value={query}
          onChangeText={handleChange}
          onBlur={handleBlur}
          onFocus={() => predictions.length > 0 && setShowList(true)}
          placeholderTextColor={RD.textMuted}
          autoComplete="off"
          autoCorrect={false}
        />
        {loading && <ActivityIndicator style={styles.loader} size="small" color={RD.maroon} />}
      </View>
      {showList && predictions.length > 0 && (
        <ScrollView style={styles.listContainer} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
          {predictions.map((item) => (
            <TouchableOpacity
              key={item.place_id}
              style={styles.row}
              onPress={() => handleSelect(item)}
            >
              <View style={styles.pinWrap}>
                <Text style={styles.pinIcon}>{'⦿'}</Text>
              </View>
              <Text style={styles.rowText} numberOfLines={2}>{item.description}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { position: 'relative', zIndex: 10 },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: RD.surface,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: RD.borderStrong,
    overflow: 'hidden',
  },
  searchIconWrap: {
    paddingLeft: 14,
    paddingRight: 4,
  },
  searchIcon: {
    fontSize: 18,
    color: RD.textMuted,
  },
  input: {
    flex: 1,
    paddingHorizontal: 10,
    paddingVertical: 14,
    fontSize: 15,
    color: RD.text,
    fontFamily: fonts.regular,
  },
  loader: { marginRight: 14 },
  listContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: RD.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: RD.borderStrong,
    marginTop: 4,
    maxHeight: 240,
    zIndex: 100,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 13,
    borderBottomWidth: 1,
    borderBottomColor: RD.border,
  },
  pinWrap: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: RD.maroonSoft,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pinIcon: {
    fontSize: 14,
    color: RD.maroon,
  },
  rowText: {
    flex: 1,
    fontSize: 14,
    color: RD.text,
    fontFamily: fonts.regular,
    lineHeight: 20,
  },
});
